// shared-components/ui/Section.jsx
import { forwardRef } from "react";
import PropTypes from "prop-types";
import { cn } from "@shared/lib/utils";
import { useSiteGradients } from "@shared/hooks";

const Section = forwardRef(
  (
    {
      className,
      children,
      fullWidth = false,
      gradient = false,
      gradientType = null,
      animation = null,
      style = {},
      noPadding = false,
      transparent = false,
      ...props
    },
    ref,
  ) => {
    // Always call hooks unconditionally
    const siteGradients = useSiteGradients();
    const gradients =
      gradient || gradientType || animation ? siteGradients : null;

    // Determine background class
    let bgClass = transparent ? "" : "bg-white dark:bg-gray-900"; // Default background unless transparent
    let animationClass = "";
    let sectionStyle = { ...style };

    if (gradient || gradientType) {
      const gradientClass =
        gradientType && gradients[gradientType]
          ? gradients[gradientType]
          : gradients?.section || "";

      bgClass = gradientClass;

      // Add animation if specified
      if (animation && gradients?.animations?.[animation]) {
        animationClass = gradients.animations[animation];

        // Add inline animation style for consistent behavior
        if (animation === "goldenPulse") {
          sectionStyle.animation =
            "pulse 1.618s cubic-bezier(0.618, 0, 0.382, 1) infinite";
        } else if (animation === "goldenSpin") {
          sectionStyle.animation = "spin 16.18s linear infinite";
        } else if (animation === "specialPulse") {
          sectionStyle.animation = "pulse 3s ease infinite";
        } else if (animation === "specialSpin") {
          sectionStyle.animation = "spin 10.8s linear infinite";
        }
      }
    }

    return (
      <section
        ref={ref}
        className={cn("py-12", bgClass, animationClass, className)}
        style={sectionStyle}
        {...props}
      >
        <div
          className={
            noPadding
              ? fullWidth
                ? ""
                : "container mx-auto"
              : fullWidth
                ? "px-2 sm:px-4"
                : "container mx-auto px-2 sm:px-4"
          }
        >
          {children}
        </div>
      </section>
    );
  },
);

Section.displayName = "Section";

Section.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  fullWidth: PropTypes.bool,
  gradient: PropTypes.bool,
  gradientType: PropTypes.string,
  animation: PropTypes.string,
  style: PropTypes.object,
  noPadding: PropTypes.bool,
  transparent: PropTypes.bool,
};

export default Section;
export { Section };
